import { AiSuggestionInventoryTypeSchema, type AiSuggestionInventoryType } from '@repo/types';
import { z } from 'zod';

/** Fiche retenue par l'utilisateur à partir des suggestions IA, transmise à l'assistant de création d'inventaire. */
export interface AiPrefill {
  suggestion_id: string;
  inventory_type: AiSuggestionInventoryType;
  /** Espèce choisie, si elle figure dans la bibliothèque ; sinon l'utilisateur la sélectionne à la création. */
  espece_id: number | null;
  /** Libellé de l'espèce proposée (affiché même quand elle n'est pas dans la bibliothèque). */
  species_label: string | null;
  /** Réponses retenues, au format du formulaire d'inventaire existant. */
  form_data: Record<string, string | string[]>;
  saved_at: number;
}

const PrefillSchema = z.object({
  suggestion_id: z.string().min(1),
  inventory_type: AiSuggestionInventoryTypeSchema,
  espece_id: z.number().int().positive().nullable(),
  species_label: z.string().nullable(),
  form_data: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
  saved_at: z.number(),
});

const KEY = (id: string) => `ai-prefill:${id}`;
/** Au-delà, la fiche est considérée périmée : l'utilisateur doit relancer l'analyse. */
export const PREFILL_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/** Mémorise la fiche pour la page de création (survit à un rechargement, disparaît avec l'onglet). */
export function saveAiPrefill(prefill: Omit<AiPrefill, 'saved_at'>, now = Date.now()): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(KEY(prefill.suggestion_id), JSON.stringify({ ...prefill, saved_at: now }));
    return true;
  } catch {
    return false;
  }
}

export function readAiPrefill(id: string | null | undefined, now = Date.now()): AiPrefill | null {
  if (!id) return null;
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(KEY(id));
    if (!raw) return null;
    const parsed = PrefillSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.suggestion_id !== id) return null;
    if (now - parsed.data.saved_at > PREFILL_MAX_AGE_MS) {
      store.removeItem(KEY(id));
      return null;
    }
    const d = parsed.data;
    // Reconstruction explicite : le type inféré par zod varie selon `strict` (apps/docs compile ces sources sans).
    return {
      suggestion_id: d.suggestion_id,
      inventory_type: d.inventory_type,
      espece_id: d.espece_id ?? null,
      species_label: d.species_label ?? null,
      form_data: d.form_data,
      saved_at: d.saved_at,
    };
  } catch {
    return null;
  }
}

export function clearAiPrefill(id: string): void {
  try {
    storage()?.removeItem(KEY(id));
  } catch {
    /* sans effet : le stockage peut être indisponible */
  }
}

/** Adresse de la page de création, pré-remplie par la fiche IA. */
export function buildCreateUrl(prefill: Pick<AiPrefill, 'suggestion_id' | 'inventory_type'>): string {
  const type = prefill.inventory_type === 'ALIGNEMENT' ? 'alignement' : 'arbre';
  return `/admin/inventaires/create?type=${type}&ai=${encodeURIComponent(prefill.suggestion_id)}`;
}
